import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Eye, MoreHorizontal, Pencil, Printer, RotateCcw, Search } from 'lucide-react';
import * as hr from '../api';
import { useHR, clearLookup, useLookup } from '../HRContext';
import { errorText } from '../../utils';
import StatusPill from '../components/StatusPill';
import { EMPLOYMENT_STATUSES, EMPLOYMENT_TYPES, actionLabel, download, fmtMoney, has, initials, monthsSince, titleCase, toCSV, today } from '../format';
import { findSubmodule, hrSubUrl } from '../catalog';
import Icon from '../Icon';
import Loader from '../components/Loader';
import { Select } from '../components/Controls';
import Pagination from '../../components/requisition/Pagination';

// employee lifecycle moves offered from the row menu (PATCH /employees/{id}/status)
export const EMPLOYEE_MOVES = {
  draft: ['onboarding'],
  onboarding: ['probation', 'active'],
  probation: ['active', 'suspended', 'exited'],
  active: ['suspended', 'inactive', 'exited'],
  suspended: ['active', 'exited'],
  inactive: ['active', 'exited'],
  exited: [],
};
const QUICK = ['attendance', 'leave', 'overtime-authorization', 'hmo', 'id-cards', 'promotions', 'transfers', 'onboarding', 'offboarding',
  'performance-reviews', 'employee-queries', 'recruitment', 'accommodation-assignments', 'employee-documents', 'attendance-report'];

// data problems worth fixing, as on the reference registry
export function warningsOf(e) {
  const out = [];
  if (!has(e.bank_account_number)) out.push('Missing Bank');
  if (!has(e.pension_rsa_pin)) out.push('No Pension ID');
  if (!has(e.tax_identification_number)) out.push('No Tax ID');
  if (!has(e.work_phone)) out.push('No Phone');
  if (!has(e.hire_date)) out.push('No Hire Date');
  if (e.employment_status === 'probation' && e.probation_end_date && e.probation_end_date < today()) out.push('Probation Overdue');
  return out;
}

function RowMenu({ e, moves, onMove, onClose, links, up }) {
  const ref = useRef(null);
  useEffect(() => {
    const away = ev => { if (ref.current && !ref.current.contains(ev.target)) onClose(); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [onClose]);
  return (
    <div className={`row-menu ${up ? 'up' : ''}`} ref={ref} onClick={ev => ev.stopPropagation()}>
      {links.map(([to, label, icon]) => <Link key={to} to={to}><Icon name={icon} size={14} /> {label}</Link>)}
      {moves.length > 0 && <div className="row-menu-sep">Change status</div>}
      {moves.map(to => (
        <button key={to} type="button" className={['exited', 'suspended'].includes(to) ? 'danger' : ''} onClick={() => onMove(e, to)}>{actionLabel(to)}</button>
      ))}
    </div>
  );
}

export default function ManageEmployees() {
  const { canManage, notify, confirm } = useHR();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState(''); // q, once typing pauses
  const [dept, setDept] = useState('');
  const [status, setStatus] = useState(params.get('status') || '');
  const [etype, setEtype] = useState('');
  const [page, setPage] = useState({ skip: 0, limit: 10 });
  const [menu, setMenu] = useState(null);
  const departments = useLookup('departments') || [];

  // the server filters and pages the registry (X-Total-Count gives the full match count)
  const filters = { search: search || undefined, department_id: dept || undefined, status: status || undefined, employment_type: etype || undefined };
  const filterKey = JSON.stringify(filters);
  // a filter change starts again from page one
  const firstPage = () => setPage(p => (p.skip ? { ...p, skip: 0 } : p));
  const filter = set => v => { set(v); firstPage(); };
  // the search box only applies when Search is clicked (or Enter pressed) — typing sends nothing
  const runSearch = ev => {
    ev?.preventDefault();
    const term = q.trim();
    if (term === search && !page.skip) load(); else { setSearch(term); firstPage(); }
  };
  const [loading, setLoading] = useState(true);
  const latest = useRef(0); // only the newest request may fill the table
  const load = () => {
    const ticket = ++latest.current;
    setError(''); setLoading(true);
    hr.listEmployeesPage({ ...filters, skip: page.skip, limit: page.limit })
      .then(r => { if (ticket === latest.current) { setRows(r.items); setTotal(r.total); } })
      .catch(e => { if (ticket === latest.current) setError(errorText(e)); })
      .finally(() => { if (ticket === latest.current) setLoading(false); });
  };
  useEffect(load, [filterKey, page.skip, page.limit]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadStats = () => hr.getEmployeeStats().then(setStats).catch(() => setStats(null));
  useEffect(() => { loadStats(); }, []);

  const enrich = e => ({ ...e, _pay: Number(e.monthly_gross ?? e.basic_salary) || 0, _months: monthsSince(e.hire_date), _warn: warningsOf(e) });
  const shown = useMemo(() => (rows || []).map(enrich), [rows]);

  // the cards cover the whole workforce, from the server's summary (the table only holds one page)
  const kpis = [
    ['Total Employees', stats?.total_employees ?? '—', 'none', 'users'],
    ['Active', stats?.active_employees ?? '—', 'good', 'user-check'],
    ['On Probation', stats?.probation_employees ?? '—', 'info', 'hourglass'],
    ['Draft / Onboarding', stats ? (stats.draft_employees || 0) + (stats.onboarding_employees || 0) : '—', 'warn', 'file-pen-line'],
    ['Missing Bank Info', stats?.paid_missing_bank ?? '—', 'bad', 'landmark', 'Need attention'],
    ['Total Salary Bill', stats ? fmtMoney(Number(stats.monthly_salary_bill) || 0) : '—', 'none', 'wallet', 'Active employees / month'],
  ];
  const totalShown = shown.reduce((t, e) => t + e._pay, 0);

  const move = async (e, to) => {
    setMenu(null);
    if (!(await confirm({ title: actionLabel(to), message: `${actionLabel(to)} ${e.full_name}?`, confirmLabel: actionLabel(to), danger: ['exited', 'suspended', 'inactive', 'terminated'].includes(to) }))) return;
    try {
      await hr.setEmployeeStatus(e.id, { status: to, effective_date: today() });
      clearLookup('employees');
      notify(`${e.full_name}: ${titleCase(to)}`);
      load(); loadStats();
    } catch (err) { notify(errorText(err), 'bad'); }
  };

  // every employee matching the filters, not just this page
  const exportCSV = async () => {
    let all;
    try { all = (await hr.listEmployees(filters)).map(enrich); } catch (err) { notify(errorText(err), 'bad'); return; }
    download('employees.csv', toCSV([
    { label: 'Employee ID', get: e => e.employee_no }, { label: 'Name', get: e => e.full_name }, { label: 'Email', get: e => e.work_email },
    { label: 'Department', get: e => e.department_name }, { label: 'Designation', get: e => e.designation_title || e.job_title }, { label: 'Grade', get: e => e.pay_grade },
    { label: 'Site', get: e => e.site_name }, { label: 'Employment Type', get: e => titleCase(e.employment_type) },
    { label: 'Salary Type', get: e => titleCase(e.salary_type) }, { label: 'Monthly Salary', get: e => e._pay },
    { label: 'Months', get: e => e._months ?? '' }, { label: 'Status', get: e => titleCase(e.employment_status) }, { label: 'Warnings', get: e => e._warn.join('; ') },
    ], all));
  };

  const reset = () => { setQ(''); setSearch(''); setDept(''); setStatus(''); setEtype(''); firstPage(); };
  const filtered = q || search || dept || status || etype;

  if (error) return <div className="error">{error}</div>;
  if (!rows) return <Loader title="Loading employees…" subtitle="Fetching the employee registry" />;

  return (
    <div className="manage-emp">
      <div className="me-head no-print">
        <div className="spacer" />
        <button className="btn" onClick={exportCSV}><Download size={15} /> Export CSV</button>
        <button className="btn" onClick={() => window.print()}><Printer size={15} /> Print</button>
      </div>

      <div className="kpi-grid me-kpis">
        {kpis.map(([label, value, tone, icon, sub]) => (
          <div key={label} className={`kpi tone-${tone}`}>
            <div className="kpi-head"><div className="kpi-label">{label}</div><span className="kpi-icon"><Icon name={icon} size={17} /></span></div>
            <div className="kpi-value">{value}</div>
            {sub && <small className="kpi-sub">{sub}</small>}
          </div>
        ))}
      </div>

      <div className="panel me-table-panel">
        <div className="toolbar no-print">
          <form className="me-search" onSubmit={runSearch}>
            <div className="search grow-search">
              <Search size={16} />
              <input placeholder="Search by name, ID, email or department..." value={q} onChange={e => setQ(e.target.value)} />
            </div>
            <button type="submit" className="btn primary" disabled={loading}><Search size={15} /> Search</button>
          </form>
          <Select value={dept} onChange={e => filter(setDept)(e.target.value)}><option value="">All departments</option>{departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</Select>
          <Select value={status} onChange={e => filter(setStatus)(e.target.value)}>
            <option value="">All statuses</option>
            {EMPLOYMENT_STATUSES.map(s => <option key={s} value={s}>{titleCase(s)}</option>)}
          </Select>
          <Select value={etype} onChange={e => filter(setEtype)(e.target.value)}>
            <option value="">All employment types</option>
            {EMPLOYMENT_TYPES.map(s => <option key={s} value={s}>{titleCase(s)}</option>)}
          </Select>
          <button className="btn ghost" onClick={reset} disabled={!filtered}><RotateCcw size={14} /> Reset</button>
          <div className="spacer" />
          <span className="muted small">{total} record{total === 1 ? '' : 's'}</span>
        </div>

        <div className="table-wrap">
          <table className="me-table">
            <thead>
              <tr><th className="no-print">Actions</th><th>Employee</th><th>Dept / Designation</th><th>Site / Branch</th><th>Salary Type</th>
                <th>Monthly Salary</th><th>Months</th><th>Status</th><th>Warnings</th></tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9}><Loader title="Loading employees…" subtitle={`Page ${Math.floor(page.skip / page.limit) + 1} of ${Math.max(1, Math.ceil(total / page.limit))}`} /></td></tr>
              )}
              {!loading && shown.map((e, i) => (
                <tr key={e.id} className="clickable" onClick={() => navigate(hrSubUrl('employee-360', { id: e.id }))}>
                  <td className="no-print actions-cell" onClick={ev => ev.stopPropagation()}>
                    <div className="row-actions-me">
                      <Link className="btn small" to={hrSubUrl('employee-360', { id: e.id })} title="Open Employee 360"><Eye size={14} /> 360</Link>
                      {canManage && <Link className="icon-btn" to={hrSubUrl('add-employee', { id: e.id })} title="Edit employee"><Pencil size={15} /></Link>}
                      <button type="button" className="icon-btn" title="More actions" onClick={() => setMenu(menu === e.id ? null : e.id)}><MoreHorizontal size={16} /></button>
                      {menu === e.id && (
                        <RowMenu e={e} moves={canManage ? EMPLOYEE_MOVES[e.employment_status] || [] : []} onMove={move} onClose={() => setMenu(null)} up={i >= 4 && i >= shown.length - 4}
                                 links={[[hrSubUrl('employee-360', { id: e.id, tab: 'attendance' }), 'Attendance', 'clock'],
                                   [hrSubUrl('employee-360', { id: e.id, tab: 'leave' }), 'Leave Records', 'calendar'],
                                   [hrSubUrl('employee-360', { id: e.id, tab: 'salary' }), 'Salary History', 'trending-up'],
                                   [hrSubUrl('employee-360', { id: e.id, tab: 'documents' }), 'Documents', 'file-badge']]} />
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="emp-cell">
                      <span className="emp-avatar">{initials(e.full_name)}</span>
                      <span><b>{e.full_name}</b><small>{e.employee_no}{e.work_email ? ` · ${e.work_email}` : ''}</small></span>
                    </div>
                  </td>
                  <td><b>{e.department_name || '—'}</b><small>{e.designation_title || e.job_title || '—'}{e.pay_grade ? ` · ${e.pay_grade}` : ''}</small></td>
                  <td><b>{e.site_name || '—'}</b><small>{e.branch_name || '—'} · {titleCase(e.employment_type) || '—'}</small></td>
                  <td>{titleCase(e.salary_type) || '—'}</td>
                  <td className="num">{fmtMoney(e._pay)}</td>
                  <td>{e._months ?? '—'}</td>
                  <td><StatusPill value={e.employment_status} /></td>
                  <td>
                    <div className="warn-chips">
                      {e._warn.length === 0 && <span className="muted small">—</span>}
                      {e._warn.slice(0, 2).map(w => <span key={w} className="warn-chip">{w}</span>)}
                      {e._warn.length > 2 && <span className="warn-chip more" title={e._warn.slice(2).join(', ')}>+{e._warn.length - 2} more</span>}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && shown.length === 0 && <tr><td colSpan={9} className="empty">{filtered ? 'No employees match these filters.' : 'No employees yet — use Add Employee to create the first record.'}</td></tr>}
            </tbody>
            {!loading && <tfoot>
              <tr>
                <td colSpan={5}>PAGE TOTALS — {shown.length} of {total} employees</td>
                <td className="num">{fmtMoney(totalShown)}</td>
                <td colSpan={3}>Active: {shown.filter(e => e.employment_status === 'active').length} | With Warnings: {shown.filter(e => e._warn.length).length}</td>
              </tr>
            </tfoot>}
          </table>
        </div>
        <div className="no-print"><Pagination total={total} skip={page.skip} limit={page.limit} onChange={setPage} /></div>
      </div>

      <div className="panel no-print">
        <h3 className="panel-title">Quick Module Access — All Employee-Related Features</h3>
        <div className="quick-grid">
          {QUICK.map(k => {
            const s = findSubmodule(k)?.sub;
            return s && <Link key={k} to={hrSubUrl(k)} className="quick-link"><Icon name={s.icon} size={18} /><span>{s.title}</span></Link>;
          })}
        </div>
      </div>
    </div>
  );
}
